import React, { JSX, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  BaseLogo,
  ImageLogo,
  LogoEditStatus,
  isChangeColor,
  isDigital,
} from "../../../../core/models/entities/BaseLogo";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCircleExclamation,
  faCircleInfo,
  faExpand,
  faRulerCombined,
} from "@fortawesome/free-solid-svg-icons";
import Tooltip from "@mui/material/Tooltip";

import { PrintMethodAutoComplete } from "./PrintMethodAutoComplete";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import {
  Color,
  LogoOptions,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import LoadingElement from "../../../components/loading-element/loading-element";
import { ColorChange } from "../../logo-list/hooks/useLogoActionsHandler";
import { LogoImage } from "../../../components/Logo/LogoImage";
import { useConfirmationDialog } from "../../../components/Dialogs/confirmation-dialog";
import DigitalColorBlock from "./DigitalColorBlock";
import LogoColorChanger from "./LogoColorChanger";
import { CustomAutoComplete } from "../../../components/Input/CustomAutoComplete";
import { VendorSearchAutoComplete } from "../../../components/Input/VendorSearchAutoComplete";
import { Organization } from "../../../../core/models/entities/Organization";
import { Role } from "../../../../core/models/entities/Role";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { LogoResize } from "./LogoResize";
import { faSave } from "@fortawesome/free-regular-svg-icons";
import { LogoRuler } from "./LogoRuler";
import { excludeNonSelectableMethods } from "../../../../utils/functions/TransferMethodUtil";
import { enviroment } from "../../../../enviroment";
import { MutationProgress } from "./MutationProgress";
import {
  formBelongsToLogo,
  getPreviewScale,
  getRulerSizeMm,
  resolveColorChanges,
} from "../functions/previewScale";
import { isEmbroideryTransferType } from "../../../../utils/systemColors";
import { LogoResizeHandle } from "./LogoResizeHandle";
import { useDragResizeCommit } from "../hooks/useDragResizeCommit";
import {
  ProductPendingBadge,
  ProductPendingLock,
} from "../../../components/Logo/ProductPendingBadge";
import { isLogoUploadPending } from "../../../../utils/functions/productPending";
import { useKeepScaleView } from "../hooks/useKeepScaleView";
import { isSharpWaitActive } from "../functions/sharpWait";
import { PriceRefreshNowButton } from "../../../components/Logo/PriceRefreshNowButton";

// Flags REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW / REACT_APP_LOGO_CLONE_SIZE_FIX
// - build-time constants, so hook order never varies at runtime.
const optimisticEnabled = enviroment.logoEditorOptimisticPreviewEnabled;
const sizeFixEnabled = enviroment.logoCloneSizeFixEnabled;
// Flag REACT_APP_LOGO_DRAG_RESIZE: the corner handle needs the live canvas
// scale of the optimistic preview, so it only exists when both are on.
const dragResizeEnabled =
  enviroment.logoDragResizeEnabled && optimisticEnabled;
// Flag REACT_APP_LOGO_DRAG_RESIZE_KEEP_SCALE: the canvas keeps the px/mm a
// corner drag ran at (true-scale view) and a button toggles the fit view.
const keepScaleEnabled =
  dragResizeEnabled && enviroment.logoDragResizeKeepScaleEnabled;
// Flag REACT_APP_CLONE_INSTANT_SHARP_WAIT: an upload-pending clone without a
// genuine high-res is painted at its natural pixel size (never stretched)
// inside a skeleton frame until the high-res lands.
const sharpWaitEnabled = enviroment.cloneInstantSharpWaitEnabled;

export interface LogoEditorTool_VM {
  options?: LogoOptions;
  logo: BaseLogo;
  onCancel: () => void;
  onDraft?: () => void;
  onCommit: () => void;
  onUpdateColor: (colors: ColorChange[]) => void;
  isLoading: boolean;
  // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: the from -> to pairs of
  // the colour change currently in flight (view-model captured; the picker
  // clears its own selection synchronously). Rendered as chips in the
  // progress strip instead of the modal overlay. null/undefined = no colour
  // change running.
  pendingColorChanges?: ColorChange[] | null;
  showCustomerInput: boolean;
  customers: Organization[];
  logoForm: ImageLogoForm;
  updateForm: (form: ImageLogoForm) => void;
  onResize: () => void;
  isInDesignEditor?: boolean;
}

function LogoEditorTool({
  options,
  logo: baseLogo,
  onDraft,
  onCommit,
  onCancel,
  onUpdateColor,
  isLoading,
  showCustomerInput,
  customers,
  logoForm,
  updateForm,
  onResize,
  isInDesignEditor = false,
  pendingColorChanges,
}: LogoEditorTool_VM): JSX.Element {
  const { t } = useTranslation();
  // Queue the logo for saving as draft.
  // Some bug causes useEffect to run twice, which results in an infinite state-update loop.
  // Not fixing this bug now, so adding a state-trigger hack instead.
  const [queued, setQueued] = useState(false);

  useEffect(() => {
    if (queued) {
      onDraft?.call(null);
      setQueued(false);
    }
    // TODO Should be using `logoForm.transferMethod` as dependency instead,
    //  but this causes the effect to run twice and loop indefinitely.
  }, [queued]);

  const { role } = useProfileContext();
  // "Preparing logo…" (flag cloneInstantEnabled) - always false with it off.
  const uploadLocked = isLogoUploadPending(baseLogo);
  // "Preparing sharp preview…" (flag cloneInstantSharpWaitEnabled): the
  // clone's only bitmap is the source's small thumbnail. Always false with
  // the flag off = today's classes, no overlay element.
  const sharpWait = sharpWaitEnabled && isSharpWaitActive(baseLogo);
  const logo = baseLogo as ImageLogo;

  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);

  // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: how far the canvas image
  // may GROW (CSS scale) and still fit the container - measured from the
  // layout boxes (offsetWidth/Height ignore transforms, so the measure is
  // stable while the scale is applied). Shrinking is always allowed. Same
  // no-deps/observer idiom as LogoRuler; the equality guard keeps it stable.
  const [maxPreviewScale, setMaxPreviewScale] = useState(1);
  useEffect(() => {
    if (!optimisticEnabled) return;
    const container = containerRef.current;
    const img = logoRef.current;
    if (!container || !img) return;
    const rulerPad = isInDesignEditor ? 0 : 24;
    const measure = () => {
      const availW = container.clientWidth - rulerPad - 8;
      const availH = container.clientHeight - rulerPad - 8;
      const ow = img.offsetWidth;
      const oh = img.offsetHeight;
      const next =
        ow > 0 && oh > 0 ? Math.max(1, Math.min(availW / ow, availH / oh)) : 1;
      setMaxPreviewScale((prev) =>
        Math.abs(prev - next) < 0.001 ? prev : next
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(img);
    return () => observer.disconnect();
  });

  function handleCommit() {
    confirmationDialog.open();
  }

  const confirmationDialog = useConfirmationDialog(
    t("logoEditorComponents.confirmSaveCommit"),
    onCommit,
  );

  const digitalTransferDialog = useConfirmationDialog(
    t("logoEditorComponents.digitalTransferWarning"),
    () => {
      if (pendingMethod) {
        applyMethodChange(pendingMethod);
        setPendingMethod(null);
      }
    },
  );

  const [pendingMethod, setPendingMethod] = useState<TransferMethod | null>(
    null,
  );

  // Flag REACT_APP_LOGO_DRAG_RESIZE: a running corner drag switches the
  // canvas transition off (ruler + handle read the rect at render); the
  // release commits through the same onResize as "Change size" once the
  // form holds the final mm, with the same <= 30x30 mm confirmation. Hooks
  // are unconditional (hook order); the dialog element renders under the
  // flag only.
  const [dragging, setDragging] = useState(false);
  const dragConfirmDialog = useConfirmationDialog(
    t("logoEditorComponents.confirmResize", {
      width: logoForm.widthMm,
      height: logoForm.heightMm,
    }),
    onResize,
  );
  const handleDragCommit = useDragResizeCommit(
    logoForm,
    handleSizeChange,
    onResize,
    dragConfirmDialog.open,
  );
  // Flag REACT_APP_LOGO_DRAG_RESIZE_KEEP_SCALE: view state only (hook order
  // unconditional; disabled = the fit view, exactly today's scale).
  const keepScale = useKeepScaleView(
    keepScaleEnabled,
    logoForm,
    logo,
    maxPreviewScale,
  );

  function handleDragStateChange(dragging: boolean) {
    setDragging(dragging);
    if (dragging && keepScaleEnabled) keepScale.onDragStart();
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    updateForm({
      ...logoForm,
      [name]: value,
    });
  }

  function handleSizeChange(newWidth: number, newHeight: number) {
    updateForm({
      ...logoForm,
      widthMm: newWidth,
      heightMm: newHeight,
    });
  }

  function onUpdateCustomer(customer?: Organization) {
    updateForm({
      ...logoForm,
      customer: customer ?? logoForm.customer,
    });
  }

  function updateColorsHandler(colors: ColorChange[]) {
    onUpdateColor(colors);
  }

  function applyMethodChange(method: TransferMethod | null) {
    if (method) {
      const [maxWidth, maxHeight] = method.size.split("x").map(Number);
      updateForm({
        ...logoForm,
        transferMethod: method,
        heightMm: logoForm.heightMm > maxHeight ? maxHeight : logoForm.heightMm,
        widthMm: logoForm.widthMm > maxWidth ? maxWidth : logoForm.widthMm,
      });
    } else {
      updateForm({
        ...logoForm,
        transferMethod: method,
      });
    }
    // Mark the logo as queued for saving as draft.
    setQueued(true);
  }

  function changePrintMethod(method: TransferMethod | null) {
    const currentIsDigital =
      logoForm.transferMethod?.typeAndKey.toLowerCase().includes("digital") ||
      false;
    const newIsDigital =
      method?.typeAndKey.toLowerCase().includes("digital") || false;

    if (!currentIsDigital && newIsDigital) {
      setPendingMethod(method);
      digitalTransferDialog.open();
      return;
    }

    applyMethodChange(method);
  }

  if (!options) {
    return <></>;
  }

  // Screen-print methods (NAV-import only) and admin-blocked methods must not
  // be selectable; the currently selected method is kept so existing/cloned
  // logos still display it.
  const filteredMethods = excludeNonSelectableMethods(
    options.transferMethods,
    logoForm.transferMethod?.key,
    options.blockedMethodKeys
  ).filter((x) => {
    // Exclude digital methods for non-digital logos
    if (isDigital(logo)) {
      return (x.method1 + x.method2 + x.method3)
        .toLowerCase()
        .includes("digital");
    }

    // Exclude "direk tryk" methods if logo has more than 5 colors
    if (logo.colors.length > 5 && x.type.toLowerCase() === "direk tryk") {
      return false;
    }

    return true;
  });

  const isColorStop2 =
    logoForm.transferMethod?.key === "COLORSTOP2" ||
    logoForm.transferMethod?.key === "COLORSTOP";

  const currentColors =
    logo.colorDetails && logo.colorDetails.length > 0
      ? logo.colorDetails
      : options.allColors.filter((c) => logo.colors.includes(c.id));

  const hasMetallicOrNeon = currentColors.some((c) => {
    const colorInList = options.allColors.find((ac) => ac.id === c.id);
    return colorInList?.isMetallic || colorInList?.isNeon;
  });

  const showColorStopWarning = isColorStop2 && hasMetallicOrNeon;

  // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: ruler + canvas scale follow
  // the size FIELDS (logoForm) while typing; the saved size (logo) stays the
  // reference the scale is computed against. Both only while the form
  // belongs to the open logo (formBelongsToLogo - the design editor can swap
  // the logo under a mounted tool). Flag off: ruler bound to the saved size
  // exactly as before and no transform on the image.
  const rulerSize = optimisticEnabled
    ? getRulerSizeMm(logoForm, logo)
    : { widthMm: logo.widthMm, heightMm: logo.heightMm };
  const rulerWidthMm = rulerSize.widthMm;
  const rulerHeightMm = rulerSize.heightMm;
  const previewScale = !optimisticEnabled
    ? 1
    : keepScaleEnabled
      ? keepScale.previewScale
      : getPreviewScale(logoForm, logo, maxPreviewScale);
  const resolvedColorChanges = optimisticEnabled
    ? resolveColorChanges(pendingColorChanges, options.allColors)
    : [];
  const mutationLabel =
    resolvedColorChanges.length > 0
      ? t("logoEditorComponents.applyingColorChange")
      : t("logoEditorComponents.savingChanges");

  return (
    <>
      {confirmationDialog.component}
      {digitalTransferDialog.component}
      {dragResizeEnabled && dragConfirmDialog.component}
      {isLoading && !optimisticEnabled && <LoadingElement transparent />}
      <div className={"h-full flex flex-col"}>
        <div
          id={"toolbar"}
          className={"bg-gray-100 space-y-2 py-2 px-3 rounded-tr-md"}
        >
          <div className={"flex items-center space-x-3"}>
            <h2 className={"flex-1 font-bold text-gray-600"}>
              {t("logoEditorComponents.editingLogo")}
            </h2>
            {/* Admin "Refresh prices now" (flags logoPriceRefreshNowEnabled +
                logoPriceRefreshNowEditorEnabled, BOTH): ONE POST per click
                that evicts the supplier caches and re-queues the price
                look-up for the open logo; the list rows the admin goes back
                to are marked "Prices on their way" (their own poll clears
                them). The lock is read from the cached detail through a
                subscription - never a fetch - and nothing here runs on
                render. No detailStatus: the editor never mounts on a stub.
                Either flag off / not admin / not an image logo = null =
                the toolbar as today. */}
            <PriceRefreshNowButton logo={baseLogo} surface="editor" />
            {/* "Preparing logo…" (flag cloneInstantEnabled): the clone still
                awaits its own supplier identity - badge here, Save & approve
                locked below. Always false with the flag off = no element. */}
            {uploadLocked && (
              <ProductPendingBadge
                variant="upload"
                pending={true}
                sinceUtc={baseLogo.uploadPendingSinceUtc}
              />
            )}
            <SecondaryButton
              width={"sm"}
              disabled={isLoading}
              onClick={onCancel}
            >
              {t("cancel")}
            </SecondaryButton>
            {logo.status === LogoEditStatus.Draft && !!onDraft && (
              <PrimaryButton
                width={"sm"}
                className={"px-4"}
                disabled={isLoading}
                onClick={onDraft}
              >
                <FontAwesomeIcon icon={faSave} />
                <span className={"flex-1"}>
                  {t("logoEditorComponents.saveDraft")}
                </span>
              </PrimaryButton>
            )}
            <ProductPendingLock
              locked={uploadLocked}
              variant="upload"
              className="inline-block"
            >
              <PrimaryButton
                width={"md"}
                className={"px-4"}
                disabled={isLoading || uploadLocked}
                onClick={uploadLocked ? undefined : handleCommit}
              >
                <FontAwesomeIcon icon={faCheck} />
                <span className={"flex-1"}>
                  {t("logoEditorComponents.saveChangesApprove")}
                </span>
              </PrimaryButton>
            </ProductPendingLock>
          </div>
          <div className={"text-gray-600 flex items-center space-x-3 text-xs"}>
            {showCustomerInput && role === Role.Vendor && (
              <div className={"flex items-center space-x-2"}>
                <label className={"uppercase"}>{t("customer")}</label>
                <CustomAutoComplete<Organization>
                  options={customers}
                  getOptionKey={(org) => org.id}
                  displayOption={(org) => org.name}
                  key={logoForm.customer?.id}
                  onChange={(org) => onUpdateCustomer(org!)}
                  value={logoForm.customer ?? null}
                  placeholder={t("logoEditorComponents.searchForCustomer")}
                />
              </div>
            )}
            {role === Role.Admin && (
              <div className={"flex items-center space-x-2"}>
                <label className={"uppercase"}>{t("vendor")}</label>
                <VendorSearchAutoComplete
                  value={logoForm.customer}
                  onChange={(org) => onUpdateCustomer(org!)}
                  placeholder={t("logoEditorComponents.searchForVendor")}
                />
              </div>
            )}

            <div className={"flex items-center space-x-2"}>
              <label className={"uppercase"}>{t("title")}</label>
              <input
                className={"input h-9"}
                disabled={isLoading}
                name={nameOf<ImageLogoForm>((lf) => lf.title)}
                type="text"
                value={logoForm.title}
                onChange={handleInputChange}
                maxLength={30}
              />
            </div>
            {isDigital(logo) && (
              <div className={"flex items-center space-x-2"}>
                <label className={"uppercase"}>{t("type")}</label>
                <button
                  className={"but-32 p-1 border rounded flex items-center"}
                >
                  <div className={"flex items-center h-5 w-5 mr-0.5"}>
                    <DigitalColorBlock />
                  </div>
                  Digital Transfer
                </button>
              </div>
            )}
            <div className={"flex items-center space-x-2 flex-1"}>
              <label className={"uppercase"}>
                {t("logoEditorComponents.method")}
              </label>
              <PrintMethodAutoComplete
                disabled={isLoading}
                value={logoForm.transferMethod?.key ?? ""}
                methods={filteredMethods}
                onChange={changePrintMethod}
              />
              <Tooltip title={<PrintMethodInfo />} placement={"bottom-start"}>
                <FontAwesomeIcon size={"xl"} icon={faCircleInfo} />
              </Tooltip>
            </div>
            <LogoResize
              width={logoForm.widthMm}
              height={logoForm.heightMm}
              onSizeChange={handleSizeChange}
              onResize={onResize}
              disabled={isLoading}
              transferMethod={logoForm.transferMethod}
            />
          </div>
          {isChangeColor(logo) && (
            <LogoColorChanger
              disabled={isLoading}
              options={options}
              logo={logo}
              onUpdateColor={updateColorsHandler}
              isInDesignEditor={isInDesignEditor}
            />
          )}
        </div>
        <div
          ref={containerRef}
          // Outside the design editor the mm rulers render as absolute 24px strips at the
          // container's top/left. Reserve that space (pt-6/pl-6 = 24px = RULER_SIZE) and clip,
          // so the image can never sit underneath the opaque ruler (top looked cut off on clone).
          className={
            "flex-1 flex justify-center items-center relative" +
            (!isInDesignEditor ? " overflow-hidden pt-6 pl-6" : "")
          }
        >
          {!isInDesignEditor && (
            <LogoRuler
              containerRef={containerRef}
              logoRef={logoRef}
              widthMm={rulerWidthMm}
              heightMm={rulerHeightMm}
            />
          )}
          {optimisticEnabled && isLoading && (
            <MutationProgress
              label={mutationLabel}
              insetForRulers={!isInDesignEditor}
              colorChanges={resolvedColorChanges}
              embroideryMethod={isEmbroideryTransferType(
                logo.transferMethod?.type
              )}
            />
          )}
          {isDigital(logo) && (
            <div className={"absolute top-0 left-0 p-2 z-20"}>
              <Tooltip placement={"right-start"} title={<IsDigitalInfo />}>
                <div>
                  <FontAwesomeIcon
                    className={"text-status-failed-1"}
                    size={"lg"}
                    icon={faCircleExclamation}
                  />
                </div>
              </Tooltip>
            </div>
          )}
          {showColorStopWarning && (
            <div className={"absolute top-0 left-0 p-2 z-20"}>
              <Tooltip
                placement={"right-start"}
                title={<ColorStopWarningInfo />}
              >
                <div>
                  <FontAwesomeIcon
                    className={"text-status-failed-1"}
                    size={"lg"}
                    icon={faCircleExclamation}
                  />
                </div>
              </Tooltip>
            </div>
          )}
          {sharpWait && (
            <div
              className={
                "absolute inset-0 flex items-center justify-center pointer-events-none" +
                (!isInDesignEditor ? " pt-6 pl-6" : "")
              }
              data-testid="sharp-wait-frame"
              role="status"
              aria-live="polite"
            >
              <div
                className={
                  "relative w-[450px] h-[325px] max-w-full max-h-full rounded " +
                  "border border-dashed border-gray-300 bg-gray-50/80 animate-pulse"
                }
              >
                <span
                  className={
                    "absolute inset-x-0 bottom-2 text-center text-xs text-gray-500"
                  }
                >
                  {t("logoEditorComponents.preparingSharpPreview")}
                </span>
              </div>
            </div>
          )}
          <LogoImage
            ref={logoRef}
            // Flag REACT_APP_LOGO_CLONE_SIZE_FIX: object-contain so the 180px
            // mutation thumbnails fill the same box the 1000px render will
            // (no size jump when the heal lands); LogoRuler measures the
            // painted rectangle either way under that flag.
            // Flag REACT_APP_CLONE_INSTANT_SHARP_WAIT (sharpWait): no min box
            // and no object-fit, so the bitmap paints at its natural pixel
            // size (max-w/max-h keep it at 1x or smaller, never upscaled),
            // above the skeleton frame. The SAME <img> element carries on
            // when the high-res lands - only its classes and src change, so
            // the switch to the full view never flickers.
            className={
              sharpWait
                ? "max-h-[650px] max-w-[900px] relative z-10"
                : (sizeFixEnabled
                    ? "min-h-[325px] min-w-[450px] max-h-[650px] max-w-[900px] object-contain"
                    : "min-h-[325px] min-w-[450px] max-h-[650px] max-w-[900px] object-scale-down") +
                  " bg-white rounded-bl"
            }
            // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: the typed size
            // scales the image live (getBoundingClientRect includes the
            // transform, so the ruler tracks it). Exactly 1 = no style at all.
            style={
              previewScale !== 1
                ? {
                    transform: `scale(${previewScale})`,
                    transformOrigin: "center",
                    transition:
                      dragResizeEnabled && dragging
                        ? "none"
                        : "transform 120ms ease-out",
                  }
                : undefined
            }
            logo={logo}
            useHighRes={true}
            onContextMenu={(e) => e.preventDefault()}
          />
          {dragResizeEnabled &&
            !isLoading &&
            !sharpWait &&
            (logo.widthMm ?? 0) > 0 &&
            (logo.heightMm ?? 0) > 0 &&
            formBelongsToLogo(logoForm, logo) && (
              <LogoResizeHandle
                containerRef={containerRef}
                logoRef={logoRef}
                widthMm={logoForm.widthMm}
                heightMm={logoForm.heightMm}
                savedWidthMm={logo.widthMm ?? 0}
                savedHeightMm={logo.heightMm ?? 0}
                transferMethodSize={logoForm.transferMethod?.size}
                disabled={isLoading}
                onPreview={handleSizeChange}
                onCommit={handleDragCommit}
                onCancel={handleSizeChange}
                onDragStateChange={handleDragStateChange}
              />
            )}
          {keepScaleEnabled && keepScale.canToggle && (
            <div
              className={
                "absolute right-0 p-2 z-20" +
                (isInDesignEditor ? " top-0" : " top-6")
              }
            >
              <Tooltip
                placement={"left"}
                title={t(
                  keepScale.trueScale
                    ? "logoEditorComponents.fitToViewHint"
                    : "logoEditorComponents.trueScaleHint"
                )}
              >
                <button
                  type="button"
                  data-testid="logo-view-toggle"
                  aria-pressed={!keepScale.trueScale}
                  className={
                    "flex items-center space-x-1.5 h-8 px-2 rounded border bg-white/90 " +
                    "text-xs text-gray-600 shadow hover:text-blue-600 hover:border-blue-600"
                  }
                  onClick={keepScale.toggle}
                >
                  <FontAwesomeIcon
                    icon={keepScale.trueScale ? faExpand : faRulerCombined}
                  />
                  <span>
                    {t(
                      keepScale.trueScale
                        ? "logoEditorComponents.fitToView"
                        : "logoEditorComponents.trueScale"
                    )}
                  </span>
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default LogoEditorTool;

function IsDigitalInfo() {
  const { t } = useTranslation();
  return (
    <div className={"text-xs text-gray-100"}>
      <p>{t("logoEditorComponents.digitalOnlyTitle")}</p>
      <br />
      <p>{t("logoEditorComponents.digitalOnlyReasonsIntro")}</p>
      <ul>
        <li>• {t("logoEditorComponents.digitalOnlyReasonNotVector")}</li>
        <li>• {t("logoEditorComponents.digitalOnlyReasonGradient")}</li>
        <li>• {t("logoEditorComponents.digitalOnlyReasonTooManyColors")}</li>
        <li>• {t("logoEditorComponents.digitalOnlyReasonOpacity")}</li>
      </ul>
      <br />
      <p>{t("logoEditorComponents.digitalOnlyMetallicNote")}</p>
    </div>
  );
}

function PrintMethodInfo() {
  const { t } = useTranslation();
  return (
    <div className={"text-xs text-gray-100 w-[160px] font-medium"}>
      <b>Standard</b> {t("logoEditorComponents.methodStandardDesc")}
      <br />
      <br />
      <b>Industrial</b> {t("logoEditorComponents.methodIndustrialDesc")}
      <br />
      <br />
      <b>Water Industrial</b>{" "}
      {t("logoEditorComponents.methodWaterIndustrialDesc")}
      <br />
      <br />
      <b>Water Reflex</b> {t("logoEditorComponents.methodWaterReflexDesc")}
      <br />
      <br />
      <b>Water Sport</b> {t("logoEditorComponents.methodWaterSportDesc")}
      <br />
      <br />
      <b>ColorStop</b> {t("logoEditorComponents.methodColorStopDesc")}
      <br />
      <br />
      <b>Digital</b> {t("logoEditorComponents.methodDigitalDesc")}
      <br />
      <br />
      <b>Digital ColorStop</b>{" "}
      {t("logoEditorComponents.methodDigitalColorStopDesc")}
      <br />
      <br />
      <b>Nylon</b> {t("logoEditorComponents.methodNylonDesc")}
      <br />
      <br />
    </div>
  );
}

function ColorStopWarningInfo() {
  const { t } = useTranslation();
  return (
    <div className={"text-xs text-gray-100"}>
      <p>{t("logoEditorComponents.colorStopWarning1")}</p>
      <br />
      <p>{t("logoEditorComponents.colorStopWarning2")}</p>
    </div>
  );
}
